import React, { useEffect, useMemo, useRef, useState } from 'react';
import { checkCars, checkHasCar, deleteOil, editOil, getAllOils, getOilTypes } from '../../app/api';
import { useOilContext } from '../../context/OilContext';
import { Button, Checkbox, Col, DatePicker, Dropdown, Form, Input, List, Modal, Radio, Row, Select, Spin, Table } from 'antd';
import { MoreOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import 'moment/locale/hy-am';
import { debounce } from '../../helpers/helpers';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const OilPage = () => {
  const { oils, setOils } = useOilContext();
  const formRef = useRef(null);
  const [dateRange, setDateRange] = useState([]);
  const [filterRange, setFilterRange] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null); 
  const [serviceType, setServiceType] = useState('');
  const [isCarRegistered, setIsCarRegistered] = useState(false);
  const [carResults, setCarResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [carName, setCarName] = useState("");
  const [isHasCar, setIsHasCar] = useState(false);
  const [allTypes, setAllTypes] = useState([]);
  const [carExistsMessage, setCarExistsMessage] = useState('');
  const [carNumberExists, setCarNumberExists] = useState(false);
  const [vinExistsMessage, setVinExistsMessage] = useState('');
  const [vinExists, setVinExists] = useState(false);
  const [userNameExistsMessage, setUserNameExistsMessage] = useState('');
  const [userNameExists, setUserNameExists] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const showFilterModal = () => setIsFilterModalOpen(true);
  const handleFilterCancel = () => setIsFilterModalOpen(false);

  const handleResetFilter = () => {
    setDateRange([]);
    setFilterRange([]);
  };

  const handleApplyFilter = () => {
    setFilterRange(dateRange);
    setIsFilterModalOpen(false);
  };

  const handleEditModalCancel = () => {
    setCarResults([]);
    setCarName("");
    setEditData(null);
    form.resetFields();
    setTimeout(() => {
      setIsEditModalOpen(false);
    }, 200);
  }

  const onEdit = (oil) => {
    setEditData(oil);
    setIsEditModalOpen(true); 

    const formattedDate = moment(oil.createdAt, "YYYY-MM-DD HH:mm:ss");
    if (!formattedDate.isValid()) {
        console.error('Invalid date format');
        return;
    }

    setServiceType(oil.serviceType);
    setCarName(oil.car?.car_number || "");
    setIsHasCar(oil.car?.car_number ? true : false);
    setIsCarRegistered(oil.car?.car_number ? false : true)

    if (!oil.car && !oil.car_number) {
      setIsCarRegistered(false)
    }

    form.setFieldsValue({
      additionalNotes: oil.additionalNotes,
      airFilter: oil.airFilter,
      cabinFilter: oil.cabinFilter,
      carData: oil.car || {},
      oilFilter: oil.oilFilter,
      car_number: oil.car_number,
      username: oil.username,
      win_number: oil.win_number,
      oil_name: oil.oil_name,
      oil_type: oil.oil_type,
      run: oil.run,
      nextRun: oil.nextRun,
      runUnit: oil.runUnit,
      serviceType: oil.serviceType,
      createdAt: formattedDate,
      isCarRegistered: !!oil.car_number
    });
  }

  const onDelete = (oilID) => {
     Modal.confirm({
      title: 'Դուք համոզվա՞ծ եք',
      okText: 'Այո',
      okType: 'danger',
      cancelText: 'Ոչ',
      onOk: async () => {
        try {
          const result = await deleteOil(oilID);
          if (result.status === 200) {
            setOils(prevOil => prevOil.filter(item => item.id !== oilID));
          }
        } catch (error) {
          console.error('Error deleting transaction:', error);
        }
      },
      onCancel() {
        console.log('Delete operation canceled');
      },
    });
  }

  const handleEdit = async  (values) => {
      const response = await editOil(editData.id, values);
  
      if (response.status === 200) {
        setIsEditModalOpen(false);
        form.resetFields();
        setEditData(null);
        getOils();
      }
  }

  const handleTableChange = (pagination) => {
      setPage(pagination.current);
      setLimit(pagination.pageSize);
  };

  const serviceTypeMap = {
    engine_oil: "Շարժիչի յուղի փոխարինում",
    transmission: "Փոխանցման տուփի փոխարինում",
    hydraulic_or_differential: "Հիդրավլիկ կամ մոստ",
  };

  const columns = [
    {
      title: '№',
      dataIndex: 'index',
      key: 'index',
      render: (text, record, index) => (page - 1) * limit + index + 1,
    },
    {
      title: 'Ավտոմեքենայի Համարանիշ',
      dataIndex: 'car_number',
      key: 'car_number',
      render: (text, record) => record.car?.car_number || record.car_number || "",
    },
    {
      title: 'Յուղի Անուն',
      dataIndex: 'oil_name',
      key: 'oil_name',
    },
    {
      title: 'Յուղի Տեսակ',
      dataIndex: 'oil_type',
      key: 'oil_type',
    },
    {
      title: 'Վազք',
      dataIndex: 'run',
      key: 'run',
      render: (text, record) => `${text} ${record.runUnit}`,
    },
    {
      title: 'Հաջորդ Վազք',
      dataIndex: 'nextRun',
      key: 'nextRun',
      render: (text, record) => `${text} ${record.runUnit}`,
    },
    {
      title: 'Սպասարկման Տեսակ',
      dataIndex: 'serviceType',
      key: 'serviceType',
      render: (text) => serviceTypeMap[text] || "Անհայտ",
    },
    {
        title: 'Նկարագրություն',
        dataIndex: 'additionalNotes',
        key: 'additionalNotes',
        render: (text) => {
          if (text && text.length > 50) {
            return text.substring(0, 50) + '...';
          }
          return text;
        },
        onCell: () => ({
          style: { 
            maxWidth: 200, 
            wordWrap: 'break-word', 
            whiteSpace: 'normal' 
          }
        })
      },
    {
      title: 'Փոխման Ամսաթիվ',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => {
        const formattedDate = moment(text).format('ll');
        const fromNow = moment(text).fromNow();
        return `${formattedDate} (${fromNow})`;
      }
    },
    {
      title: "Գործողություններ",
      key: "actions",
      align: "center",
      render: (_, record) => {
        const menuItems = [
          {
            key: "edit",
            label: "✏️ Խմբագրել",
            onClick: () => onEdit(record),
          },
          {
            key: "delete",
            label: "🗑️ Ջնջել",
            danger: true,
            onClick: () => onDelete(record.id),
          },
        ];
  
        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];  

  const handleSearch = useMemo(() => 
        debounce(async (value) => {
          if (!value) {
            setCarResults([]);
            return;
          }
          
          setIsLoading(true);
          try {
            const response = await checkHasCar(value);
            if (response.data.success) {
              const carData = response.data.data;
              setCarResults(carData.length > 0 ? carData : []);
            }
          } catch (error) {
            console.error("Error during search:", error);
          }
          setIsLoading(false);
        }, 500), 
  []);

  const handleSelectCar = (car) => {
      setCarName(car.car_number);
      setCarResults([]);
      if (car) {
          setIsHasCar(true);
      } else {
          setIsHasCar(false);
      }
  };

  const getOils = async () => {
    try {
        setLoading(true);
        const data = {
            startDate: dateRange.length ? dateRange[0].format("YYYY-MM-DD") : "",
            endDate: dateRange.length ? dateRange[1].format("YYYY-MM-DD") : "",
            page,
            limit
          }
          const allData = await getAllOils(data);
          const all_cars = allData.data;
          if (allData.status === 200) {
              setOils(all_cars?.data);
              setTotal(all_cars.total);
              if (all_cars.data.length === 0) {
                setDisabled(true);
              } else {
                setDisabled(false);
              }
          }
    } catch(err) {
        console.log("Error", err);
    } finally {
        setLoading(false);
    }
  };

  const allOilTypes = async () => {
      const allOilTypes = await getOilTypes();
      const oil_types_data = allOilTypes.data;
      if (allOilTypes.status === 200) {
          setAllTypes(oil_types_data.data);
      }
  };

  const validateVin = (vin) => {
    const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
    if (!vinRegex.test(vin)) {
        return 'Սխալ VIN համարը։ Այն պետք է լինի 17 նշան և պարունակի միայն թույլատրված նշաններ։';
    }
    return 'VIN համարը ճիշտ է։';
  };

  const checkCarInfo = async (field, value) => {
      if (value) {
          try {
              let response;
              if (field === "username") {
                  await new Promise((resolve) => {
                      debounce(async (value) => {
                          response = await checkCars(field, value);
                          resolve();
                      }, 800)(value);
                  });
              } else {
                  response = await checkCars(field, value);
              }

              if (!response.data.success) {
                  if (field === 'win_number' && value.trim().toLowerCase() !== editData?.win_number.trim().toLowerCase()) {
                      setVinExists(true);
                      setVinExistsMessage('Նման VIN համարը արդեն գոյություն ունի։');
                  }
                  if (field === 'car_number' && value.trim().toLowerCase() !== editData?.car_number.trim().toLowerCase()) {
                      setCarNumberExists(true);
                      setCarExistsMessage('Նման մեքենայի համարանիշը արդեն գոյություն ունի։');
                  }
                  if (field === "username" && value.trim().toLowerCase() !== editData?.username.trim().toLowerCase()) {
                      setUserNameExists(true);
                      setUserNameExistsMessage('Նման օգտանունով արդեն գոյություն ունի։');
                  }
              } else {
                  if (field === 'win_number') {
                      setVinExists(false);
                      setVinExistsMessage('');
                  }
                  if (field === 'car_number') {
                      setCarNumberExists(false);
                      setCarExistsMessage('');
                  }
                  if (field === 'username') {
                      setUserNameExists(false);
                      setUserNameExistsMessage('');
                  }
              }
          } catch (error) {
              console.error('Error checking car info: ', error);
          }
      }
  };

  const carNumberValidator = (value) => {
    const firstPart = /^[0-9]{2}/;
    const secondPart = /^[A-Z]{2}/;
    const thirdPart = /^[0-9]{3}$/;

    if (value) {
        const upperCaseValue = value.toUpperCase();
        
        if (!firstPart.test(upperCaseValue.slice(0, 2))) {
            return Promise.reject('Առաջին երկու բնագավառները պետք է լինեն թվեր');
        }
        
        if (!secondPart.test(upperCaseValue.slice(2, 4))) {
            return Promise.reject('Երկու հաջորդ բնագավառները պետք է լինեն մեծատառեր');
        }
        
        if (!thirdPart.test(upperCaseValue.slice(4, 7))) {
            return Promise.reject('Վերջին երեք բնագավառները պետք է լինեն թվեր');
        }
    }

    return Promise.resolve();
  };

  useEffect(() => {
      allOilTypes();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getOils()
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterRange]);

  useEffect(() => {
    formRef.current?.setFieldsValue({
      isCarRegistered: isCarRegistered,
    });
}, [isCarRegistered]);
  

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <Button disabled={disabled} icon={<FilterOutlined />} onClick={showFilterModal}>
          Ֆիլտր
        </Button>
        {filterRange.length > 0 && (
          <Button icon={<ReloadOutlined />} onClick={handleResetFilter}>
            Մաքրել ֆիլտրը
          </Button>
        )}
      </div>

      <Modal title="Ֆիլտր" open={isFilterModalOpen} onCancel={handleFilterCancel} footer={null}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <RangePicker format="YYYY-MM-DD" value={dateRange.length ? dateRange : null} onChange={(dates) => setDateRange(dates || [])} placeholder={["Սկիզբ", "Վերջ"]} />
            <Button type="primary" onClick={handleApplyFilter} block>
              Հաստատել
            </Button>
          </div>
      </Modal>


      <Modal
        title="Խմբագրել Տեղեկություն"
        open={isEditModalOpen}
        onCancel={handleEditModalCancel}
        footer={null}
      >
        <Form layout="vertical" ref={formRef} onFinish={handleEdit} form={form}>
          <Row gutter={16}>
              <Col span={12}>
                  <Form.Item
                      label="Սպասարկման տեսակ"
                      name="serviceType"
                      initialValue="engine_oil"
                  >
                      <Select
                          placeholder="Ընտրեք սպասարկման տեսակը"
                          value={serviceType}
                          onChange={(value) => {
                              setServiceType(value);
                          }}
                      >
                          <Select.Option value="engine_oil">Շարժիչի յուղի փոխարինում</Select.Option>
                          <Select.Option value="transmission">Փոխանցման տուփի փոխարինում</Select.Option>
                          <Select.Option value="hydraulic_or_differential">Հիդրավլիկ կամ մոստ</Select.Option>
                      </Select>
                  </Form.Item>
              </Col>

              <Col span={12}>
                  <Form.Item
                      label="Մեքենայի համարանիշ"
                      name="carData"
                      rules={[
                          {
                          required: !isCarRegistered,
                          message: 'Մեքենայի համարանիշը պարտադիր է',
                          },
                      ]}
                  >
                      <Dropdown
                          overlay={
                             carResults.length > 0 ? (
                                  <List
                                      bordered
                                      dataSource={carResults}
                                      renderItem={(item) => (
                                          <List.Item
                                              onClick={() => handleSelectCar(item)}
                                              style={{ background: "#fff", cursor: "pointer" }}
                                          >
                                              {item.username} 
                                              <span style={{ color: "red" }}> {item.car_number} </span> 
                                          </List.Item>
                                      )}
                                      style={{ width: 260, maxHeight: 200, overflowY: "auto", background: "#808080" }}
                                  />
                              ) : isLoading ? (
                                  <Spin />
                              ) : (
                                  carName && carResults.length === 0 && !isLoading && (
                                      <div style={{ padding: 10 }}>Տվյալներ չեն գտնվել</div>
                                  )
                              )
                          }
                          open={(carResults.length > 0 || isLoading) && isEditModalOpen}
                      >
                          <Input
                              placeholder="Մեքենայի համարանիշ"
                              disabled={isLoading}
                              value={carName}
                              onChange={(e) => {
                                  setCarName(e.target.value);
                                  handleSearch(e.target.value);
                                  if (e.target.value === "") {
                                      setIsHasCar(false)
                                  } else {
                                    formRef.current?.resetFields(['isCarRegistered']);
                                    formRef.current?.resetFields(['username']);
                                    formRef.current?.resetFields(['car_number']);
                                    formRef.current?.resetFields(['win_number']);
                                    setIsCarRegistered(false);
                                  }
                              }}
                          />
                      </Dropdown>
                  </Form.Item>
              </Col>
          </Row>

          <Row gutter={16}>
              <Col span={12}>
                  <Form.Item 
                      label="Յուղի անվանում"
                      name="oil_name"
                      rules={[{ required: true, message: 'Խնդրում ենք մուտքագրել յուղի անվանումը!' }]}
                  >
                      <Input placeholder="Մուտքագրեք յուղի անվանումը" />
                  </Form.Item>
              </Col>

              <Col span={12}>
                  <Form.Item
                      label="Յուղի տեսակը"
                      name="oil_type"
                      rules={[{ required: true, message: 'Խնդրում ենք ընտրել յուղի տեսակը' }]}
                  >
                      {serviceType === "engine_oil" ? <Select
                          showSearch
                          placeholder="Ընտրել յուղի տեսակը"
                          optionFilterProp="children"
                          onChange={(value) => {
                              // setSelectedMake(value);
                              form.setFieldsValue({ model: undefined });
                          }}
                          filterOption={(input, option) =>
                              option?.children.toLowerCase().includes(input.toLowerCase())
                          }
                      >
                          {allTypes.map((item) => (
                              <Select.Option key={item.id} value={item.oil}>
                                  {item.oil}
                              </Select.Option>
                          ))}
                      </Select> : <Input placeholder="Խնդրում ենք ընտրել յուղի տեսակը" /> }
                  </Form.Item>
              </Col>
          </Row>

          <Row gutter={16}>
              <Col span={12}>
                  <Form.Item 
                      label="Վազքը (կմ / մղոն)" 
                      name="run" 
                      rules={[
                          { required: true, message: 'Խնդրում ենք մուտքագրել վազքը!' },
                          { pattern: /^[0-9]+$/, message: 'Մուտքագրեք միայն թվեր!' }
                      ]}
                  >
                      <Input type="number" placeholder="Մուտքագրեք վազքը (կմ / մղոն)" />
                  </Form.Item>
              </Col>

              <Col span={12}>
                  <Form.Item 
                      label="Հաջորդ փոխման վազքը" 
                      name="nextRun" 
                      rules={[
                          { required: true, message: 'Խնդրում ենք մուտքագրել հաջորդ փոխման վազքը!' },
                          { pattern: /^[0-9]+$/, message: 'Մուտքագրեք միայն թվեր!' },
                      ]}
                  >
                      <Input 
                          type="number" 
                          placeholder="Մուտքագրեք հաջորդ փոխման վազքը (կմ / մղոն)"
                      />
                  </Form.Item>
              </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                  label="Ամսաթիվ"
                  name="createdAt"
              >
                  <DatePicker style={{ width: '100%' }} disabled placeholder='Ընտրել ամսաթիվը' />
              </Form.Item>
            </Col>

            <Col span={12}>
                <Form.Item label="Վազքի միավոր" name="runUnit">
                    <Radio.Group>
                        <Radio value="km">Կմ</Radio>
                        <Radio value="mile">Մղոն</Radio>
                    </Radio.Group>
                </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Ֆիլտրեր">
              <Row gutter={16}>
                  <Col span={8}>
                      <Form.Item
                          name="oilFilter"
                          valuePropName="checked"
                          initialValue={false}
                          noStyle
                      >
                          <Checkbox>Յուղի ֆիլտր</Checkbox>
                      </Form.Item>
                  </Col>
                  { serviceType === "engine_oil" ? 
                      <>
                          <Col span={8}>
                              <Form.Item
                                  name="cabinFilter"
                                  valuePropName="checked"
                                  initialValue={false}
                                  noStyle
                              >
                                  <Checkbox>Սրահի ֆիլտր</Checkbox>
                              </Form.Item>
                          </Col>
                          <Col span={8}>
                              <Form.Item
                                  name="airFilter"
                                  valuePropName="checked"
                                  initialValue={false}
                                  noStyle
                              >
                                  <Checkbox>Օդի ֆիլտր</Checkbox>
                              </Form.Item>
                          </Col>
                        </> : null }
              </Row>
          </Form.Item>

          <Form.Item
              name="additionalNotes"
              label="Հավելյալ նշումներ"
          >
              <TextArea 
                  autoSize={{ minRows: 3, maxRows: 5 }} 
                  placeholder="Մուտքագրեք ավելին..." 
              />
          </Form.Item>

          {!isHasCar ? <Form.Item name="isCarRegistered" valuePropName="checked">
              <Checkbox
                  onChange={(e) => {
                      setIsCarRegistered(e.target.checked);
                      if (!e.target.checked) {
                          form.setFieldsValue({
                              username: '', 
                              car_number: '',  
                              win_number: '',   
                          });
                      } else {
                        setCarName("");
                        setCarResults([])
                        form.setFieldsValue({
                          username: editData?.username, 
                          car_number: editData?.car_number,  
                          win_number: editData?.win_number,   
                      });
                    }
                  }}
              >
                  Մեքենան գրանցվա՞ծ է պահեստում
              </Checkbox>
          </Form.Item> : null}

          {isCarRegistered && <div
              style={{
                  marginBottom: '20px',
                  padding: '15px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '8px',
                  backgroundColor: '#fafafa',
              }}
              >
              <h3 style={{ marginBottom: '15px' }}>Հաճախորդի Տվյալներ</h3>
                  <Row gutter={16}>
                      <Col span={12}>
                          <Form.Item
                              label="Օգտատերի Անուն"
                              name="username"
                              rules={[
                                  {
                                      validator: async (_, value) => {
                                            checkCarInfo('username', value);
                                      },
                                  },
                              ]}
                          >
                              <Input placeholder="Օրինակ՝ Աշոտ" onChange={(e) => setUserNameExistsMessage("")} />
                          </Form.Item>
                          {userNameExists && <span style={{ color: 'red' }}>{userNameExistsMessage}</span>}
                      </Col>

                      <Col span={12}>
                          <Form.Item
                              label="Մեքենայի համարանիշ"
                              name="car_number"
                              rules={[
                                  {
                                      validator: async (_, value) => {
                                          await carNumberValidator(value);
                                          checkCarInfo('car_number', value);
                                      },
                                  },
                              ]}
                          >
                              <Input 
                                  placeholder="Օրինակ՝ 13AB666"
                                  style={{ textTransform: 'uppercase' }}
                                  maxLength={7}
                                  onChange={(e) => {
                                      setCarExistsMessage("");
                                      let value = e.target.value.toUpperCase();
                                      if (/[^A-Z0-9]/.test(value)) {
                                          value = value.replace(/[^A-Z0-9]/g, '');
                                      }

                                      if (value.length <= 8) {
                                          e.target.value = value;
                                      }
                                  }}
                              />
                          </Form.Item>
                          {carNumberExists && <span style={{ color: 'red' }}>{carExistsMessage}</span>}
                      </Col>
                  </Row>

                  <Row gutter={16}>
                      <Col span={12}>
                          <Form.Item
                              label="VIN համար"
                              name="win_number"
                              rules={[
                                  {
                                      validator: async (_, value) => {
                                          await validateVin(value);
                                          checkCarInfo('win_number', value);
                                      },
                                  },
                              ]}
                          >
                              <Input placeholder="Օրինակ՝ JM1GJ1U62E1423456" onChange={(e) => setVinExistsMessage("")} />
                          </Form.Item>
                          {vinExists && <span style={{ color: 'red' }}>{vinExistsMessage}</span>}
                      </Col>
                  </Row>
          </div>}
          <Button type="primary" htmlType="submit" block >
            Խմբագրել
          </Button>
        </Form>
      </Modal>

      <Table
          columns={columns}
          dataSource={oils}
          rowKey="id"
          pagination={{
              current: page,
              pageSize: limit,
              total: total
          }}
          loading={loading}
          scroll={{ x: 'max-content' }}
          onChange={handleTableChange}
      />
    </div>
  );
};

export default OilPage;
